import React from "react";
import { IconButton } from "@mui/material";
import { Close } from "@mui/icons-material";
import { useTranslation } from "react-i18next";

/**
 * GeneralInfoBox
 * A reusable info/dialog box component.
 *
 * Props:
 * - title: string | ReactNode — top heading
 * - body: string | ReactNode — main content
 * - children: ReactNode — optional custom content between body and footer
 * - footer: ReactNode — footer actions (buttons, etc.)
 * - onClose: function — callback for close button click
 * - showClose: boolean — whether to show the close icon (default: true)
 * - className, style, maxWidth: styling options
 */
export default function GeneralInfoBox({
    title,
    body,
    children = null,
    footer = null,
    onClose = null,
    showClose = true,
    className = "",
    style = {},
    maxWidth = "28rem",
}) {
    const { t } = useTranslation()


    return (
        <div
            className={`p-4 text-center shadow-lg ${className}`}
            style={{
                background: "linear-gradient(180deg, #F5FBF9 0%, #FFFFFF 100%)",
                borderRadius: 16,
                border: "1px solid #E6F0EC",
                maxWidth,
                margin: "0 auto",
                position: "relative",
                ...style,
            }}
            role="dialog"
            aria-modal="true"
        >
            {/* ===== Close button (optional) ===== */}
            {showClose && onClose && (
                <IconButton
                    onClick={onClose}
                    size="small"
                    sx={{
                        position: "absolute",
                        top: 8,
                        right: 8,
                        background: "rgba(0,0,0,0.06)",
                        "&:hover": { background: "rgba(0,0,0,0.15)" },
                    }}
                    aria-label="Close"
                >
                    <Close />
                </IconButton>
            )}

            {/* ===== Title ===== */}
            {title && (
                <p
                    className="fw-bold"
                    style={{ fontSize: "1.5rem", lineHeight: 1.4, marginBottom: "0.75rem" }}
                >
                    {t(title)}
                </p>
            )}

            {/* ===== Body ===== */}
            {body && (
                <p
                    id="generalInfoBoxBody"
                    style={{ fontSize: "0.95rem", marginBottom: "1.25rem" }}
                >
                    {t(body)}
                </p>
            )}

            {/* ===== Middle (custom) ===== */}
            {children}

            {/* ===== Footer ===== */}
            {footer && <div style={{ marginTop: "1rem" }}>{footer}</div>}
        </div>
    );
}
